import { filter, first, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ObservableQueryResult, Updater } from '@ngneat/query';

import { IPageResponse } from '../types';

export function fromQuery<R, E>(query: ObservableQueryResult<R, E>): Observable<R | undefined> {
  return query.pipe(
    filter((res) => res.status !== 'pending'),
    switchMap((result) => {
      if (result.error) {
        return throwError(() => result.error);
      }

      return of(result.data);
    }),
    first(),
  );
}

/**
 * An operator that takes values emitted by the source observable
 * until the `isFetching` property on the result is false and return query data value or error value.
 * It is intended to be used in scenarios where an observable stream should be listened to
 * until the result has finished fetching (e.g success or error).
 */
export function takeResultOnce<R, E, T extends ObservableQueryResult<R, E>>(error?: ((errorData: any) => R) | R) {
  return (source$: T): Observable<R> => {
    return source$.pipe(
      filter((query) => query.status !== 'pending'),
      map((query) => {
        if (query.status === 'error') {
          if (error) {
            return typeof error === 'function' ? (error as Function)(query.error) : (error as R);
          }
          throw query.error;
        }

        return query.data!;
      }),
      first(),
    );
  };
}

export function updateEntity<T extends Record<string, any>>(
  entityData: Partial<T>,
): Updater<T | undefined, T | undefined> {
  return function (state: T | undefined) {
    if (!state) return;

    return { ...state, ...entityData } as T;
  };
}

export function addListEntity<T extends Record<string, any>>(
  entity: T,
  atTop = false,
): Updater<T[] | unknown, T[] | unknown> {
  return function (state: T[] | { results: T[]; [key: string]: unknown } | undefined) {
    if (!state) return;

    if (!Array.isArray(state)) {
      return {
        ...state,
        results: atTop ? [entity, ...state.results] : [...state.results, entity],
      };
    }

    return atTop ? [entity, ...state] : [...state, entity];
  };
}

export function updateListEntity<Model extends Record<string, any>, Updates extends Partial<Model> = Partial<Model>>(
  entityId: string | number,
  entityData: Updates,
  entityIdKey: keyof Model = 'id',
): Updater<Model[] | unknown, Model[] | unknown> {
  return function (state: Model[] | { results: Model[]; [key: string]: unknown } | undefined) {
    if (!state) return;

    const list = !Array.isArray(state) ? [...((state as any).results as Model[])] : [...state];
    const idx = list?.findIndex((el: Model) => el[entityIdKey] === entityId);

    if (idx > -1) {
      list[idx] = { ...list[idx], ...entityData };
    }

    return !Array.isArray(state) ? { ...state, results: list } : list;
  };
}

export function removeListEntity<T extends Record<string, any>>(
  entityId: string | number,
  entityIdKey: keyof T = 'id',
): Updater<T[] | unknown, T[] | unknown> {
  return function (state: T[] | { results: T[]; [key: string]: unknown } | undefined) {
    if (!state) return;

    if (!Array.isArray(state)) {
      return {
        ...state,
        results: state.results.filter((el) => el[entityIdKey] !== entityId),
      };
    }

    return state.filter((el) => el[entityIdKey] !== entityId);
  };
}

export function getQueryListEntity<Type>(
  state: Type[] | IPageResponse<Type> | undefined,
  entityId: string | number,
  entityIdKey = 'id',
) {
  if (!state) return null;

  const list: Type[] = Array.isArray(state) ? state : state?.results || [];

  return list.find((item) => item[entityIdKey as keyof Type] === entityId) || null;
}

export function updateInfinitePageItem<T extends Record<string, any>>(
  entityId: string | number,
  entityData: Partial<T>,
  entityIdKey: keyof T = 'id',
): Updater<T[] | unknown, T[] | unknown> {
  return function (state: { pages: { results: T[] }[] }) {
    if (!state) return;

    return {
      ...state,
      pages: state.pages.map((p: { results: T[] }) => {
        return {
          ...p,
          results: p.results.map((el: T) => {
            if (el[entityIdKey] === entityId) {
              return {
                ...el,
                ...entityData,
              };
            }

            return el;
          }),
        };
      }),
    };
  };
}
